import { useCallback } from 'react';

import { setItem } from '@/store/ServerStorage';

import { THEME_PREFERENCE_KEY } from './colorMode.constants';
import {
  applyThemeToDocument,
  resolveTheme,
  runThemeTransition,
} from './colorMode.utils';
import type { ResolvedTheme, ThemePreference } from './colorMode.constants';
import {
  useResolvedTheme,
  useThemePreferenceValue,
  useThemeStore,
} from './themeStore';

interface UseThemePreferenceResult {
  preference: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
}

/**
 * The user's theme choice. Setting it crossfades the page to the new theme
 * and persists it to the server-side user preferences so it follows them
 * across devices.
 */
export const useThemePreference = (): UseThemePreferenceResult => {
  const preference = useThemePreferenceValue();
  const resolvedTheme = useResolvedTheme();

  const setPreference = useCallback((next: ThemePreference) => {
    // Crossfade the switch. The attribute is applied inside the transition so
    // the browser can snapshot old and new; `ThemeSync` then finds it already
    // set. The store update keeps React in step.
    runThemeTransition(() => {
      const { systemPrefersDark } = useThemeStore.getState();
      applyThemeToDocument(resolveTheme(next, systemPrefersDark));
      useThemeStore.getState().setPreference(next);
    });
    setItem(THEME_PREFERENCE_KEY, next);
  }, []);

  return { preference, resolvedTheme, setPreference };
};
